import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocale } from "@/hooks/useLocale";
import { localizedPath, isHomePathname } from "@/i18n/languages";
import { MAIN_SITE_URL, ARTICLE_SLUG, SITE_NAME } from "@/constants";

export default function Footer() {
  const { t } = useTranslation();
  const lang = useLocale();
  const location = useLocation();
  const onHome = isHomePathname(location.pathname);

  const sectionHref = (id: string) =>
    onHome ? `#${id}` : `${localizedPath(lang, "/")}#${id}`;

  const guideHref = localizedPath(lang, `/${ARTICLE_SLUG}`);

  const exploreLinks = [
    { id: "categories", key: "footer.categories" },
    { id: "guides", key: "nav.guides" },
    { id: "about", key: "footer.about" },
    { id: "faq", key: "footer.faq" },
  ];

  return (
    <footer className="border-t border-background-200 bg-background-100">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <Link to={localizedPath(lang, "/")} className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-500">
                <i className="ri-table-2 text-lg text-white" />
              </span>
              <span className="font-heading text-lg font-semibold text-foreground-950">
                {SITE_NAME}
              </span>
            </Link>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-foreground-600">
              {t("footer.tagline")}
            </p>
          </div>

          <div>
            <h4 className="font-heading text-sm font-semibold text-foreground-950">
              {t("footer.explore")}
            </h4>
            <ul className="mt-4 space-y-2.5">
              <li>
                <a href={localizedPath(lang, "/")} className="text-sm text-foreground-600 hover:text-primary-600">
                  {t("footer.home")}
                </a>
              </li>
              {exploreLinks.map((link) => (
                <li key={link.id}>
                  <a href={sectionHref(link.id)} className="text-sm text-foreground-600 hover:text-primary-600">
                    {t(link.key)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-sm font-semibold text-foreground-950">
              {t("footer.resources")}
            </h4>
            <ul className="mt-4 space-y-2.5">
              <li>
                <Link to={guideHref} className="text-sm text-foreground-600 hover:text-primary-600">
                  {t("footer.guide")}
                </Link>
              </li>
              <li>
                <a
                  href={MAIN_SITE_URL}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-foreground-600 hover:text-primary-600"
                >
                  {t("nav.cta")}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-sm font-semibold text-foreground-950">
              {t("footer.legal")}
            </h4>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              {t("footer.disclaimer")}
            </p>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-2 border-t border-background-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-foreground-500">
            © {new Date().getFullYear()} {SITE_NAME}. {t("footer.rights")}
          </p>
          <p className="text-xs text-foreground-500">{t("footer.disclaimer")}</p>
        </div>
      </div>
    </footer>
  );
}